import DownloadReportScreen from "@/components/reports/DownloadReportScreen.vue";
import axios from "axios";
import {downloadXLSX} from "@/utils/helpers.js";
import {checkReportExportCancelled, forEachReportPage, MAX_REPORT_ROWS, REPORT_PAGE_SIZE, yieldReportExport} from "@/utils/tableReportExport.js";

/** Выбор формата и скачивание отчетов, общие для отчетов по одной и нескольким камерам. */
export const downloadReportMixin = {
  data() {
    return {
      isExportingXLSX: false,
      isReportDownloadDialogOpen: false,
      xlsxExportProgress: {active: false, percent: 0, page: 0, pages: 0, rows: 0, phase: "loading", error: ""},
    };
  },
  beforeDestroy() {
    this.cancelReportDownload();
  },
  methods: {
    openDownloadReportScreen() {
      if (this.isReportDownloadDialogOpen || this.xlsxExportProgress.active) return;
      Object.assign(this.xlsxExportProgress, {active: false, percent: 0, page: 0, pages: 0, rows: 0, phase: "loading", error: ""});
      this.isReportDownloadDialogOpen = true;
      this.$camsdals.open(
        DownloadReportScreen,
        {
          progress: this.xlsxExportProgress,
          onDownload: format => this.startReportDownload(format),
          onCancel: () => this.cancelReportDownload(),
        },
        {dialogTitle: this.$t('downloadReport'), className: "report-download-dialog"},
        {
          size: "xs",
          name: "js-click-outside report-download-vuedal",
          escapable: false,
          closeOnBackdrop: false,
          onClose: () => {
            this.isReportDownloadDialogOpen = false;
          },
        },
      );
    },

    async startReportDownload(format) {
      if (this.xlsxExportProgress.active || this._isDestroyed || this._isBeingDestroyed) return false;
      const run = this.createReportExportRun();
      Object.assign(this.xlsxExportProgress, {active: true, percent: 0, page: 0, pages: 0, rows: 0, phase: "loading", error: ""});
      try {
        if (format === "xlsx") {
          await this.downloadTableAsXLSXReport();
        } else if (format === "csv") {
          await this.downloadTableAsCSVReport();
        } else {
          throw new Error(this.$t('archiveError'));
        }
        this.checkReportDownloadCancelled();
        if (this.xlsxExportProgress.phase !== "complete") {
          if (!this.xlsxExportProgress.error) this.handleReportExportError(new Error(this.$t('archiveError')));
          return false;
        }
        return true;
      } catch (error) {
        this.handleReportExportError(error);
        return false;
      } finally {
        this.xlsxExportProgress.active = false;
        if (this.$_reportExportRun === run) this.$_reportExportRun = null;
      }
    },

    createReportExportRun() {
      const run = {cancelled: false, source: axios.CancelToken.source()};
      this.$_reportExportRun = run;
      return run;
    },

    getReportExportRequestConfig() {
      return this.$_reportExportRun ? {cancelToken: this.$_reportExportRun.source.token} : {};
    },

    checkReportDownloadCancelled() {
      checkReportExportCancelled(() => this.$_reportExportRun?.cancelled || this._isDestroyed || this._isBeingDestroyed);
    },

    cancelReportDownload() {
      const run = this.$_reportExportRun;
      if (!run || run.cancelled || !this.xlsxExportProgress.active) return;
      run.cancelled = true;
      Object.assign(this.xlsxExportProgress, {phase: "cancelling", error: ""});
      run.source.cancel("REPORT_EXPORT_CANCELLED");
    },

    setReportExportProgress({page, pages, rows}) {
      this.checkReportDownloadCancelled();
      Object.assign(this.xlsxExportProgress, {
        page, pages, rows,
        percent: Math.max(this.xlsxExportProgress.percent, Math.min(95, Math.floor(page / Math.max(1, pages) * 95))),
      });
    },

    completeReportExport() {
      this.checkReportDownloadCancelled();
      Object.assign(this.xlsxExportProgress, {percent: 100, phase: "complete", active: false});
    },

    handleReportExportError(error) {
      if (this.$_reportExportRun?.cancelled || error?.code === "REPORT_EXPORT_CANCELLED" || axios.isCancel(error)
        || this._isDestroyed || this._isBeingDestroyed) {
        Object.assign(this.xlsxExportProgress, {phase: "cancelled", error: "", active: false});
        return;
      }
      const message = error?.response?.data?.fields?.non_field_errors?.[0]
        || error?.fields?.non_field_errors?.[0]
        || error?.message
        || this.$t('archiveError');
      Object.assign(this.xlsxExportProgress, {phase: "error", error: message, active: false});
      if (!this.isReportDownloadDialogOpen && error?.code !== "REPORT_EXPORT_CANCELLED"
        && !this._isDestroyed && !this._isBeingDestroyed) {
        this.$camsdals.alert(message);
      }
    },

    async downloadReportAsXLSX({filename, headers, rowBuilder = row => row, loadPage, rows, isCancelled = () => false}) {
      const ownsRun = !this.$_reportExportRun;
      const run = this.$_reportExportRun || this.createReportExportRun();
      const cancelled = () => run.cancelled || this._isDestroyed || this._isBeingDestroyed || isCancelled();
      const progress = this.xlsxExportProgress;
      const updateProgress = details => this.setReportExportProgress(details);
      let writer;
      try {
        checkReportExportCancelled(cancelled);
        Object.assign(progress, {active: true, percent: 0, page: 0, pages: 0, rows: 0, phase: "loading", error: ""});
        // Paint the spinner before loading XLSX support or starting requests.
        await yieldReportExport();
        checkReportExportCancelled(cancelled);
        const {createXLSXReport} = await import("@/utils/xlsxReport.js");
        checkReportExportCancelled(cancelled);
        writer = createXLSXReport(headers);
        const appendPage = async records => {
          checkReportExportCancelled(cancelled);
          writer.appendRows(records.map(rowBuilder));
          await yieldReportExport();
        };
        if (Array.isArray(rows)) {
          if (rows.length > MAX_REPORT_ROWS) {
            const error = new Error();
            error.code = "REPORT_EXPORT_TOO_MANY_ROWS";
            throw error;
          }
          for (let offset = 0; offset < rows.length; offset += REPORT_PAGE_SIZE) {
            await appendPage(rows.slice(offset, offset + REPORT_PAGE_SIZE));
            updateProgress({
              page: Math.floor(offset / REPORT_PAGE_SIZE) + 1,
              pages: Math.ceil(rows.length / REPORT_PAGE_SIZE),
              rows: Math.min(offset + REPORT_PAGE_SIZE, rows.length),
            });
          }
        } else {
          await forEachReportPage(loadPage, appendPage, cancelled, updateProgress);
        }
        checkReportExportCancelled(cancelled);
        progress.phase = "finalizing";
        progress.percent = 99;
        await yieldReportExport();
        checkReportExportCancelled(cancelled);
        const fileData = writer.finish();
        downloadXLSX(filename, fileData);
        this.completeReportExport();
      } catch (error) {
        if (writer) writer.abort();
        if (cancelled() || error?.code === "REPORT_EXPORT_CANCELLED" || axios.isCancel(error)) {
          Object.assign(progress, {phase: "cancelled", error: ""});
          checkReportExportCancelled(() => true);
        }
        let reportError = error;
        if (error?.code === "REPORT_EXPORT_TOO_MANY_ROWS") {
          reportError = new Error(this.$t('maxRowLimit') + MAX_REPORT_ROWS);
        }
        if (error?.code === "REPORT_EXPORT_INVALID_RESPONSE") {
          reportError = new Error(this.$t('loadingError'));
        }
        Object.assign(progress, {phase: "error", error: reportError?.message || this.$t('archiveError')});
        throw reportError;
      } finally {
        progress.active = false;
        if (ownsRun && this.$_reportExportRun === run) this.$_reportExportRun = null;
      }
    },

    async downloadXLSXReport(options, loadingKey = "isLoadingReport") {
      if (this.isExportingXLSX || this._isDestroyed || this._isBeingDestroyed) return;
      this.isExportingXLSX = true;
      this[loadingKey] = true;
      try {
        await this.downloadReportAsXLSX(options);
      } catch (error) {
        this.handleReportExportError(error);
      } finally {
        this[loadingKey] = false;
        this.isExportingXLSX = false;
      }
    },
  },
};
